import React, { useEffect, useState, useMemo, useRef } from "react";
import { useAuth, consumeLogoutReason } from "./AuthContext";
import { useToast } from "./ToastContext";

// In prod (same-origin) we want REACT_APP_API_URL="" so URLs become "/api/...".
// Use `||` (not `??`) so an empty-string env value falls through to "" — i.e. relative
// /api/... paths. `??` previously baked "http://localhost:6080" into prod bundles when
// the env was blank, which is exactly the F-26 regression we hit twice. Memory and
// audit both flag this; standardise on `||` across the codebase.
const API_URL = process.env.REACT_APP_API_URL || "";

// UI-facing PVARA statuses mapped to backend Application.status enum values.
// Order matters — this is also the pipeline order shown in the dropdown.
const STATUS_OPTIONS = [
  { label: "New",         value: "submitted",          color: "bg-gray-100 text-gray-700" },
  { label: "Shortlisted", value: "shortlisted",        color: "bg-blue-100 text-blue-800" },
  { label: "Contacted",   value: "contacted",          color: "bg-indigo-100 text-indigo-800" },
  { label: "Interviewed", value: "interview-complete", color: "bg-purple-100 text-purple-800" },
  { label: "Offered",     value: "offer",              color: "bg-amber-100 text-amber-800" },
  { label: "Hired",       value: "hired",              color: "bg-green-100 text-green-800" },
  { label: "Rejected",    value: "rejected",           color: "bg-red-100 text-red-800" },
];
const STATUS_BY_VALUE = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

// Run async fn against ids in capped-concurrency chunks (avoid overwhelming the API).
async function chunkAll(ids, fn, chunkSize = 6) {
  const results = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    const slice = ids.slice(i, i + chunkSize);
    const out = await Promise.all(slice.map(fn));
    results.push(...out);
  }
  return results;
}

// Build & download a CSV from rows; columns are the keys passed in.
function downloadCsv(filename, rows, cols) {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(Array.isArray(v) ? v.join("|") : v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [cols.map(c => c.label).join(",")];
  rows.forEach(r => lines.push(cols.map(c => esc(typeof c.get === "function" ? c.get(r) : r[c.key])).join(",")));
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Sortable <th> button — click toggles sort direction; shows ▲/▼/⇅.
function SortableHeader({ id, sortBy, sortDir, onSort, className = "", align = "left", children }) {
  const active = sortBy === id;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "⇅";
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={"px-4 py-2 " + (align === "right" ? "text-right " : "") + className}>
      <button
        type="button"
        onClick={() => onSort(id)}
        className={"inline-flex items-center gap-1 uppercase text-xs hover:text-gray-700 " + justify + " " + (active ? "text-gray-800" : "text-gray-500")}
      >
        <span>{children}</span>
        <span className={"text-[9px] " + (active ? "text-gray-700" : "text-gray-400")}>{arrow}</span>
      </button>
    </th>
  );
}

function formatDate(isoOrNull) {
  if (!isoOrNull) return "—";
  try {
    const d = new Date(isoOrNull);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(isoOrNull);
  }
}

// "5m ago", "3h ago", "2d ago", or "12 Apr" if older than a week.
function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}


function LoginGate() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  // One-shot reason from a previous forced logout (e.g. session expired).
  const [logoutReason] = useState(() => consumeLogoutReason());

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const res = await login({ username, password });
      if (!res.ok) {
        setErr(res.message || "Login failed");
        setSubmitting(false);
        return;
      }
      // Hard-reload so PvaraRubricView mounts fresh with user already in context
      // (avoids any first-render race between auth propagation and the data fetch effect).
      window.location.reload();
    } catch (e) {
      setErr(e.message || "Login failed");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="bg-white shadow-xl rounded-2xl border border-gray-200 w-full max-w-md p-8">
        <div className="text-center mb-6">
          <div className="text-2xl font-bold text-green-700">PVARA</div>
          <div className="text-xs text-gray-500 tracking-wide uppercase">Rubric Screening · Staff Login</div>
        </div>
        {logoutReason && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            {logoutReason}
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Username</label>
            <input
              type="text"
              autoFocus
              required
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
              placeholder="admin"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            />
          </div>
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !username || !password}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold py-2.5 rounded-lg transition"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-4 text-[11px] text-gray-400 text-center">
          PVARA Rubric Screening · CONFIDENTIAL · For Official Use Only
        </p>
      </div>
    </div>
  );
}

const ROLES = [
  { id: "md", title: "Managing Director", short: "MD" },
  { id: "gm_policy_strategy", title: "GM — Policy & Strategy", short: "Policy" },
  { id: "gm_licensing_regulatory_sandbox", title: "GM — Licensing & Reg Sandbox", short: "Licensing" },
  { id: "gm_market_supervision", title: "GM — Market Supervision", short: "Mkt Sup" },
  { id: "gm_enforcement", title: "GM — Enforcement", short: "Enforce" },
  { id: "gm_technology_cyber_risk", title: "GM — Technology & Cyber Risk", short: "Tech" },
  { id: "gm_support_services", title: "GM — Support Services", short: "Support" },
];

// Tier ordering / labels — used by the Chairperson view's tier filter and
// per-role tier badges. The backend's chairman-shortlist response carries the
// tier per role (so we don't need to derive it here from ROLES).
const TIER_ORDER = ["MD", "GM", "SM", "Mgr"];
const TIER_LABEL = { MD: "Managing Director", GM: "General Manager", SM: "Senior Manager", Mgr: "Manager" };

// (auth header injection moved into AuthContext.apiFetch — see useAuth())

function colorForPercent(p) {
  if (p == null) return "text-gray-400";
  if (p >= 60) return "text-green-700";
  if (p >= 45) return "text-yellow-700";
  if (p >= 30) return "text-orange-700";
  return "text-red-700";
}

function bgForPercent(p) {
  if (p == null) return "bg-gray-100";
  if (p >= 60) return "bg-green-100";
  if (p >= 45) return "bg-yellow-100";
  if (p >= 30) return "bg-orange-100";
  return "bg-red-100";
}

function StatusDropdown({ applicationId, current, onChanged }) {
  const { apiFetch } = useAuth();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function change(newValue) {
    if (newValue === current) return;
    setSaving(true); setErr(null);
    try {
      const res = await apiFetch(`${API_URL}/api/applications/${applicationId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newValue }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onChanged(newValue);
    } catch (e) {
      // Suppress the post-logout "session-expired" throw — UI is reloading anyway.
      if (e.message !== "session-expired") setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const currentMeta = STATUS_BY_VALUE[current] || { label: current || "—", color: "bg-gray-100 text-gray-700" };

  return (
    <div>
      <div className="flex items-center gap-2">
        <select
          value={current || "submitted"}
          disabled={saving}
          onChange={(e) => change(e.target.value)}
          className={"text-xs rounded px-2 py-1 border border-gray-300 " + currentMeta.color}
        >
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
          {/* legacy statuses not in our simplified list — keep current visible if not in list */}
          {!STATUS_BY_VALUE[current] && current && (
            <option value={current}>{current}</option>
          )}
        </select>
        {saving && <span className="text-[10px] text-gray-400">saving…</span>}
      </div>
      {err && <div className="text-[10px] text-red-600 mt-1">Failed: {err}</div>}
    </div>
  );
}

function NotesPanel({ applicationId, notes, onAdded }) {
  const { apiFetch } = useAuth();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function add() {
    const t = text.trim();
    if (!t) return;
    setSubmitting(true); setErr(null);
    try {
      const res = await apiFetch(`${API_URL}/api/applications/${applicationId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      onAdded(data.note);
      setText("");
    } catch (e) {
      if (e.message !== "session-expired") setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="font-semibold text-gray-800 text-sm mb-2">Notes ({notes.length})</div>
      <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
        {notes.length === 0 && (
          <div className="text-xs text-gray-400 italic">No notes yet.</div>
        )}
        {[...notes].reverse().map((n, i) => (
          <div key={n.id || i} className="bg-gray-50 rounded px-3 py-2">
            <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">{n.text}</div>
            <div className="text-[10px] text-gray-500 mt-1">
              {formatDate(n.timestamp)}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note about this candidate…"
          rows={2}
          className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-green-500 outline-none resize-y"
        />
        <button
          onClick={add}
          disabled={submitting || !text.trim()}
          className="px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-xs font-semibold rounded"
        >
          {submitting ? "…" : "Add"}
        </button>
      </div>
      {err && <div className="text-[10px] text-red-600 mt-1">Failed: {err}</div>}
    </div>
  );
}

function RubricItemRow({ item }) {
  const rule = item.rule || {};
  let tiersDisplay = null;
  if (rule.type === "tier_table" && Array.isArray(rule.tiers)) {
    tiersDisplay = rule.tiers.map((t, i) => (
      <span key={i} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 mb-1 text-[11px]">
        <b>{t.score}</b> · {t.match}
      </span>
    ));
  } else if (rule.type === "count_bracket" && Array.isArray(rule.counts)) {
    tiersDisplay = rule.counts.map((c, i) => (
      <span key={i} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 mb-1 text-[11px]">
        <b>{c.score}</b> · ≥ {c.min} item{c.min === 1 ? "" : "s"}
      </span>
    ));
  } else if (rule.type === "years_bracket" && Array.isArray(rule.years)) {
    tiersDisplay = rule.years.map((y, i) => (
      <span key={i} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 mb-1 text-[11px]">
        <b>{y.score}</b> · ≥ {y.min} years
      </span>
    ));
  } else if (rule.type === "percentage_bracket" && Array.isArray(rule.percents)) {
    tiersDisplay = rule.percents.map((p, i) => (
      <span key={i} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 mb-1 text-[11px]">
        <b>{p.score}</b> · ≥ {p.min}%
      </span>
    ));
  } else if (rule.type === "boolean_threshold") {
    tiersDisplay = (
      <span className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 text-[11px]">
        <b>{rule.award_if_met}</b> if ≥ {rule.threshold_years} years, else 0
      </span>
    );
  }
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-gray-800">{item.label}</div>
        <div className="text-xs font-mono text-gray-500 whitespace-nowrap">Max {item.max_marks}</div>
      </div>
      {tiersDisplay && (
        <div className="mt-1.5 flex flex-wrap text-gray-700">{tiersDisplay}</div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Chairperson view
//
// Read-only digest of every Shortlisted candidate across every role. Pulls
// from /api/pvara-rubric/chairman-shortlist, which lazy-extracts a small
// summary {currentPosition, organisation, education} per candidate and
// caches it on the application doc. Status changes still happen in the
// per-role review tab — this view just surfaces what's been shortlisted.
//
// Visual language matches the rest of the screening UI: white cards,
// slate-800 header strips, gray-50 table headers, green-600 only as accent.
// ----------------------------------------------------------------------------
// Statuses with their own tab in the chairperson view. "submitted" (= New)
// is excluded — pre-review candidates aren't chairman business. "rejected"
// gets a tab so the chairman can audit rejection decisions, but it's
// excluded from the default "All Advanced" view so the active pipeline
// stays clean.
const CHAIRMAN_STATUSES = ["shortlisted", "contacted", "interview-complete", "offer", "hired", "rejected"];

function ChairpersonView() {
  const { apiFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" = all advanced
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  // The currently-open notes modal — null means closed.
  const [notesFor, setNotesFor] = useState(null);  // {row, roleId} or null
  // Refs (not state) — we don't want a re-render when these flip.
  const inFlightRef = useRef(false);
  const dataRef     = useRef(null);   // mirrors `data`, but readable inside load()

  async function load(force = false) {
    // De-dupe: skip if a fetch is already in flight (unless user explicitly
    // pressed the manual Refresh button, which should always run).
    if (inFlightRef.current && !force) return;
    inFlightRef.current = true;
    // Only show the full-screen loading skeleton on the *first* load.
    // Subsequent background refreshes update silently — no flicker.
    const showSpinner = !dataRef.current;
    if (showSpinner) setLoading(true);
    if (force)       setRefreshing(true);
    setError(null);
    try {
      const url = `${API_URL}/api/pvara-rubric/chairman-shortlist${force ? "?force_refresh=true" : ""}`;
      const res = await apiFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      dataRef.current = d;
      setData(d);
      setLastLoadedAt(new Date());
    } catch (e) {
      if (e.message !== "session-expired") setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
      if (force)       setRefreshing(false);
      inFlightRef.current = false;
    }
  }

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  // Auto-poll every 30s so status changes made by other admins (or in another
  // tab) flow into the chairperson view without a manual refresh.
  useEffect(() => {
    const id = setInterval(() => load(false), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  // Re-fetch when the browser tab regains focus or becomes visible — covers
  // the common flow of changing a status in another tab then switching back.
  useEffect(() => {
    function refetchIfVisible() {
      if (document.visibilityState === "visible") load(false);
    }
    window.addEventListener("focus", refetchIfVisible);
    document.addEventListener("visibilitychange", refetchIfVisible);
    return () => {
      window.removeEventListener("focus", refetchIfVisible);
      document.removeEventListener("visibilitychange", refetchIfVisible);
    };
    // eslint-disable-next-line
  }, []);

  // Also re-fetch whenever the user switches status tabs OR tier filter —
  // they're most likely expecting fresh data at those moments.
  useEffect(() => {
    if (data) load(false);
    // eslint-disable-next-line
  }, [statusFilter, tierFilter]);

  const filteredRoles = useMemo(() => {
    if (!data?.roles) return [];
    const q = search.trim().toLowerCase();
    return data.roles
      .filter(r => !tierFilter || r.tier === tierFilter)
      .map(r => {
        let rows = r.rows || [];
        if (statusFilter) {
          // Specific tab selected → show only that status
          rows = rows.filter(row => row.status === statusFilter);
        } else {
          // Default "All Advanced" view hides rejected — they have their own tab
          rows = rows.filter(row => row.status !== "rejected");
        }
        if (q) {
          rows = rows.filter(row =>
            (row.name || "").toLowerCase().includes(q) ||
            (row.currentPosition || "").toLowerCase().includes(q) ||
            (row.organisation || "").toLowerCase().includes(q) ||
            (row.education || "").toLowerCase().includes(q)
          );
        }
        return { ...r, rows, count: rows.length };
      })
      .filter(r => (r.rows || []).length > 0);
  }, [data, search, tierFilter, statusFilter]);

  const tierTotals = useMemo(() => {
    const out = { MD: 0, GM: 0, SM: 0, Mgr: 0 };
    (data?.roles || []).forEach(r => { out[r.tier] = (out[r.tier] || 0) + (r.count || 0); });
    return out;
  }, [data]);

  // Export only what's currently visible (respects status / tier / search filters).
  function exportCsv() {
    const rows = [];
    filteredRoles.forEach(r => {
      (r.rows || []).forEach(row => {
        rows.push({
          tier: r.tier,
          role: r.roleTitle,
          rank: row.rank,
          name: row.name,
          status: STATUS_BY_VALUE[row.status]?.label || row.status || "",
          currentPosition: row.currentPosition,
          organisation: row.organisation,
          education: row.education,
          score: row.score,
          max: row.max,
          percent: row.percent,
        });
      });
    });
    downloadCsv("pvara-chairperson.csv", rows, [
      { key: "tier",            label: "Tier" },
      { key: "role",            label: "Role" },
      { key: "rank",            label: "Rank" },
      { key: "name",            label: "Name" },
      { key: "status",          label: "Status" },
      { key: "currentPosition", label: "Current Position" },
      { key: "organisation",    label: "Organisation" },
      { key: "education",       label: "Education" },
      { key: "score",           label: "Score" },
      { key: "max",             label: "Max" },
      { key: "percent",         label: "Percent" },
    ]);
  }

  return (
    <div className="space-y-4">
      {/* Toolbar — same card chrome as the per-role view */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-5 border border-gray-100">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Chairperson Review</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Every candidate marked <b>Shortlisted</b>, grouped by post.
              Read-only — to add or remove a name, change their status from the post's own tab.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="search"
              placeholder="search name / position / org / education"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-72"
            />
            <select
              value={tierFilter}
              onChange={e => setTierFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
            >
              <option value="">All tiers</option>
              {TIER_ORDER.map(t => (
                <option key={t} value={t}>
                  {TIER_LABEL[t]} ({tierTotals[t] || 0})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={exportCsv}
              disabled={loading || !data}
              className="px-3 py-2 rounded-lg text-sm font-medium border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Export the entire shortlist as CSV"
            >
              ⬇ CSV
            </button>
            <button
              type="button"
              onClick={() => load(true)}
              disabled={loading || refreshing}
              className="px-3 py-2 rounded-lg text-sm font-medium border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Reload from server — picks up any status changes made elsewhere"
            >
              {refreshing ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
          {data && (() => {
            const rejected = data.statusCounts?.rejected || 0;
            const advanced = (data.totalShortlisted || 0) - rejected;
            return (
              <>
                <span><b className="text-gray-800">{advanced}</b> in active pipeline</span>
                {rejected > 0 && (
                  <span className="text-red-700">+ <b>{rejected}</b> rejected</span>
                )}
                <span><b className="text-gray-800">{data.rolesWithRows}</b> post{data.rolesWithRows === 1 ? "" : "s"} with at least one candidate</span>
                {data.enrichedNow > 0 && (
                  <span className="text-blue-700">enriched {data.enrichedNow} CV summary{data.enrichedNow === 1 ? "" : "ies"} just now</span>
                )}
                {lastLoadedAt && (
                  <span className="ml-auto">loaded {lastLoadedAt.toLocaleTimeString()}</span>
                )}
              </>
            );
          })()}
        </div>

        {/* Status tab strip — chairman switches between pipeline stages */}
        {data && (
          <div className="mt-4 -mx-1 overflow-x-auto">
            <div className="flex gap-1 px-1 min-w-min">
              <button
                type="button"
                onClick={() => setStatusFilter("")}
                className={
                  "shrink-0 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border transition whitespace-nowrap " +
                  (statusFilter === ""
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
                }
                title="All advanced statuses (everything except New and Rejected)"
              >
                All Advanced
                <span className={"ml-1.5 text-[10px] " + (statusFilter === "" ? "text-green-100" : "text-gray-500")}>
                  ({(data.totalShortlisted || 0) - (data.statusCounts?.rejected || 0)})
                </span>
              </button>
              {CHAIRMAN_STATUSES.map(s => {
                const meta = STATUS_BY_VALUE[s] || { label: s };
                const count = data.statusCounts?.[s] ?? 0;
                const isActive = statusFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={
                      "shrink-0 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border transition whitespace-nowrap " +
                      (isActive
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
                    }
                  >
                    {meta.label}
                    <span className={"ml-1.5 text-[10px] " + (isActive ? "text-green-100" : "text-gray-500")}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500 border border-gray-100">
          Loading shortlist… {data == null && "(first load may take a moment if CV summaries need extracting)"}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Failed to load chairman shortlist: {error}
        </div>
      )}

      {!loading && !error && filteredRoles.length === 0 && (
        <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500 border border-gray-100">
          No shortlisted candidates {search ? "match this filter." : "yet — mark candidates as Shortlisted from the per-role tabs to populate this view."}
        </div>
      )}

      {!loading && filteredRoles.map(role => (
        <ChairpersonRoleSection
          key={role.roleId}
          role={role}
          onOpenNotes={(row) => setNotesFor({ row, roleId: role.roleId })}
          onStatusChanged={() => load(false)}
        />
      ))}

      {notesFor && (
        <ChairpersonNotesModal
          row={notesFor.row}
          onClose={() => setNotesFor(null)}
          onSaved={() => { load(false); }}
        />
      )}
    </div>
  );
}

function ChairpersonNotesModal({ row, onClose, onSaved }) {
  const { apiFetch } = useAuth();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [localNotes, setLocalNotes] = useState(row.notes || []);

  async function add() {
    const t = text.trim();
    if (!t) return;
    setSubmitting(true); setErr(null);
    try {
      const res = await apiFetch(`${API_URL}/api/applications/${row._id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Optimistic-append the new note so the user sees it immediately
      setLocalNotes(prev => [...prev, data.note || { id: Date.now().toString(), text: t, timestamp: new Date().toISOString() }]);
      setText("");
      onSaved && onSaved();
    } catch (e) {
      if (e.message !== "session-expired") setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">Chairperson notes for</div>
            <div className="text-lg font-bold text-gray-900 truncate">{row.name || "—"}</div>
            <div className="text-xs text-gray-500 mt-0.5">{row.currentPosition?.split("\n")[0] || ""}</div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none ml-4"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50">
          {localNotes.length === 0 && (
            <div className="text-center text-sm text-gray-400 italic py-8">
              No comments yet. Be the first to add one.
            </div>
          )}
          {localNotes.map((n, i) => (
            <div key={n.id || i} className="bg-white rounded-lg border border-gray-200 px-4 py-3">
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{n.text}</div>
              <div className="text-[11px] text-gray-400 mt-1.5">
                {n.timestamp ? new Date(n.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment for this candidate…"
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none resize-y"
          />
          {err && <div className="text-xs text-red-600 mt-1">Failed: {err}</div>}
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Done
            </button>
            <button
              onClick={add}
              disabled={!text.trim() || submitting}
              className="px-4 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition"
            >
              {submitting ? "Adding…" : "Add comment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChairpersonRoleSection({ role, onOpenNotes, onStatusChanged }) {
  // Strip the "GM — " / "SM — " prefix from the title since the tier badge
  // shows it separately. e.g. "GM — Enforcement" → "Enforcement".
  const titleSansTier = (role.roleTitle || role.roleId || "")
    .replace(/^(GM|SM|Manager|MD|Managing Director)\s*[—-]\s*/i, "");
  // Tier badge — pastel pill that matches the status-badge pattern used elsewhere
  // (bg-{color}-100 text-{color}-700).
  const tierColor = {
    MD:  "bg-purple-100 text-purple-800",
    GM:  "bg-blue-100 text-blue-800",
    SM:  "bg-emerald-100 text-emerald-800",
    Mgr: "bg-amber-100 text-amber-800",
  }[role.tier] || "bg-gray-100 text-gray-800";

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
      {/* Section header — white card style consistent with JobList / per-role view */}
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide " + tierColor}>
            {role.tier === "Mgr" ? "MANAGER" : role.tier}
          </span>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{titleSansTier || "—"}</h3>
        </div>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {role.count} candidate{role.count === 1 ? "" : "s"}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-center w-12">Rank</th>
              <th className="px-3 py-2 text-left w-44">Name</th>
              <th className="px-3 py-2 text-left w-28">Status</th>
              <th className="px-3 py-2 text-left">Current Position</th>
              <th className="px-3 py-2 text-left">Organisation</th>
              <th className="px-3 py-2 text-left">Education</th>
              <th className="px-3 py-2 text-center w-12">CV</th>
              <th className="px-3 py-2 text-right w-24">PVARA Score</th>
              <th className="px-3 py-2 text-center w-20">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {role.rows.map((row) => {
              return (
                <tr key={row._id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-center font-bold text-gray-900 align-top">{row.rank}</td>
                  <td className="px-3 py-2 font-semibold text-gray-900 align-top">
                    {row.name || "—"}
                    {(row.redFlags && row.redFlags.length > 0) && (
                      <span className="ml-1 text-red-600" title={row.redFlags.join("\n")}>🚩</span>
                    )}
                    {row.source && row.source !== "pvara" && (
                      <span
                        className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 align-middle uppercase tracking-wide"
                        title={`Imported from ${row.source}`}
                      >
                        {row.source}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <StatusDropdown
                      applicationId={row._id}
                      current={row.status}
                      onChanged={() => onStatusChanged && onStatusChanged()}
                    />
                  </td>
                  <td className="px-3 py-2 align-top text-gray-800 whitespace-pre-wrap break-words">
                    {row.currentPosition || <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top text-gray-800 whitespace-pre-wrap break-words">
                    {row.organisation || <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top text-gray-700 whitespace-pre-wrap break-words text-xs">
                    {row.education || <span className="text-gray-400 italic">—</span>}
                  </td>
                  <td className="px-3 py-2 align-top text-center">
                    {row.cv ? (
                      <a
                        href={row.cv}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline text-xs"
                        title="Open CV in new tab"
                      >
                        ⬇
                      </a>
                    ) : (
                      <span className="text-gray-300 text-xs" title="No CV on file">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-right text-xs font-mono">
                    {row.score != null ? (
                      <span className={colorForPercent(row.percent)}>
                        {Math.round(row.score)}{row.max ? `/${row.max}` : ""}
                        {row.percent != null && (
                          <div className="text-[10px] text-gray-500">{Math.round(row.percent)}%</div>
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-center">
                    <button
                      type="button"
                      onClick={() => onOpenNotes && onOpenNotes(row)}
                      className={
                        "inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition " +
                        ((row.notesCount || 0) > 0
                          ? "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                          : "bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-gray-200")
                      }
                      title={(row.notesCount || 0) > 0 ? `${row.notesCount} comment${row.notesCount === 1 ? "" : "s"}` : "Add a comment"}
                    >
                      <span>💬</span>
                      <span>{(row.notesCount || 0) > 0 ? row.notesCount : "+"}</span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PvaraRubricView() {
  const { user, apiFetch } = useAuth();
  const { addToast } = useToast();
  // Top-level view: per-role review (default) vs chairperson digest of shortlisted.
  const [viewMode, setViewMode] = useState("rubric");  // "rubric" | "chairperson"
  const [activeRole, setActiveRole] = useState("md");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({});
  const [rubricSpec, setRubricSpec] = useState(null);
  const [rubricOpen, setRubricOpen] = useState(false);
  // Column sort + filter + selection
  const [sortBy, setSortBy]     = useState("score");        // score|percent|name|applied|status|flags
  const [sortDir, setSortDir]   = useState("desc");
  const [statusFilter, setStatusFilter] = useState("");     // "" = all
  const [flagsOnly, setFlagsOnly]       = useState(false);
  const [dateFrom, setDateFrom]         = useState("");     // YYYY-MM-DD; "" = no lower bound
  const [dateTo,   setDateTo]           = useState("");     // YYYY-MM-DD; "" = no upper bound
  const [selectedIds, setSelectedIds]   = useState(() => new Set());
  const [bulkBusy, setBulkBusy]         = useState(false);
  // Bulk-action result is surfaced via toast now (see applyBulkStatus); no inline state needed.
  const selectAllRefDesktop = useRef(null);
  const selectAllRefMobile  = useRef(null);

  // (Auth gate is at the JSX bottom — keep all hooks unconditional above this line)

  // `reloadTick` lets the auto-poll / focus-listener / status-changed hooks
  // trigger a refetch without changing activeRole.
  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick(t => t + 1);
  // Refs keep load() flicker-free: skip when in-flight; suppress the loading
  // skeleton on every refresh after the first.
  const itemsLoadedRef = useRef(false);
  const itemsInFlightRef = useRef(false);

  // Load list whenever role changes OR an explicit reload is triggered.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    // Switching role → counts as a "first load" again (different dataset).
    // Tick-only triggers → silent background refresh.
    const isRoleSwitch = !itemsLoadedRef.current; // first time for this role?
    async function load() {
      if (itemsInFlightRef.current) return;
      itemsInFlightRef.current = true;
      const showSpinner = isRoleSwitch;
      if (showSpinner) setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(
          `${API_URL}/api/pvara-rubric/applications?role_id=${activeRole}&limit=5000`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setItems(data.items || []);
          itemsLoadedRef.current = true;
        }
      } catch (e) {
        if (!cancelled && e.message !== "session-expired") setError(e.message);
      } finally {
        if (!cancelled && showSpinner) setLoading(false);
        itemsInFlightRef.current = false;
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeRole, reloadTick]);

  // Reset the "first load" flag when activeRole flips so we DO show the
  // skeleton briefly while the new role's data arrives.
  useEffect(() => { itemsLoadedRef.current = false; }, [activeRole]);

  // Auto-poll every 30s so changes from other admins (chairperson view, other
  // tabs, other users) propagate without a manual refresh.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(reload, 30000);
    return () => clearInterval(id);
  }, [user]);

  // Refetch on browser-tab focus / visibility-change — covers switching
  // tabs after a status change in the chairperson view (or anywhere else).
  useEffect(() => {
    if (!user) return;
    function refetchIfVisible() {
      if (document.visibilityState === "visible") reload();
    }
    window.addEventListener("focus", refetchIfVisible);
    document.addEventListener("visibilitychange", refetchIfVisible);
    return () => {
      window.removeEventListener("focus", refetchIfVisible);
      document.removeEventListener("visibilitychange", refetchIfVisible);
    };
  }, [user]);

  // Load full rubric spec whenever role changes
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function loadSpec() {
      try {
        const res = await apiFetch(`${API_URL}/api/pvara-rubric/roles/${activeRole}`);
        if (res.ok) {
          const d = await res.json();
          if (!cancelled) setRubricSpec(d.role || null);
        }
      } catch (_) { /* ignore */ }
    }
    loadSpec();
    return () => { cancelled = true; };
  }, [activeRole, user]);

  // One-time per-role count load
  useEffect(() => {
    if (!user) return;
    async function loadCounts() {
      const out = {};
      for (const r of ROLES) {
        try {
          const res = await apiFetch(`${API_URL}/api/pvara-rubric/applications?role_id=${r.id}&limit=1`);
          if (res.ok) {
            const d = await res.json();
            // `total` is the full match count (limit-independent); fall back to `count`
            // for older backends that didn't expose it.
            out[r.id] = (typeof d.total === "number") ? d.total : d.count;
          }
        } catch (_) { /* ignore */ }
      }
      setCounts(out);
    }
    loadCounts();
  }, []);

  async function openBreakdown(appId) {
    setBreakdown({ loading: true });
    setBreakdownLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/pvara-rubric/applications/${appId}/breakdown`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setBreakdown(d);
    } catch (e) {
      if (e.message !== "session-expired") setBreakdown({ error: e.message });
    } finally {
      setBreakdownLoading(false);
    }
  }

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = items;
    if (q) {
      arr = arr.filter(i =>
        (i.name || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.cnic || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      arr = arr.filter(i => (i.status || "submitted") === statusFilter);
    }
    if (flagsOnly) {
      arr = arr.filter(i => (i.flags || []).length > 0);
    }
    if (dateFrom || dateTo) {
      // Both bounds are local-day inclusive: 'from' midnight start, 'to' end-of-day.
      const fromMs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : -Infinity;
      const toMs   = dateTo   ? new Date(dateTo   + "T23:59:59.999").getTime() : +Infinity;
      arr = arr.filter(i => {
        if (!i.submittedAt) return false;
        const t = new Date(i.submittedAt).getTime();
        return Number.isFinite(t) && t >= fromMs && t <= toMs;
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (it) => {
      switch (sortBy) {
        case "name":    return (it.name || "").toLowerCase();
        case "percent": return typeof it.percent === "number" ? it.percent : -1;
        case "applied": return it.submittedAt ? new Date(it.submittedAt).getTime() : 0;
        case "status":  return it.status || "";
        case "flags":   return (it.flags || []).length;
        case "score":
        default:        return typeof it.score === "number" ? it.score : -1;
      }
    };
    return [...arr].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    });
  }, [items, search, statusFilter, flagsOnly, dateFrom, dateTo, sortBy, sortDir]);

  // Click a header — toggle direction if active, otherwise pick a sensible default.
  function handleSort(id) {
    if (sortBy === id) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(id);
      // Numeric / date columns default to desc (highest / newest first); textual to asc.
      setSortDir(["score", "percent", "applied", "flags"].includes(id) ? "desc" : "asc");
    }
  }

  // Selection helpers — selection is per-role; cleared whenever the role tab changes
  // or filtered set changes drastically.
  useEffect(() => { setSelectedIds(new Set()); }, [activeRole]);

  // Drive the select-all checkbox's indeterminate flag (DOM-only attribute).
  useEffect(() => {
    const total = filteredItems.length;
    let sel = 0;
    for (const it of filteredItems) if (selectedIds.has(it._id)) sel++;
    const indet = sel > 0 && sel < total;
    if (selectAllRefDesktop.current) selectAllRefDesktop.current.indeterminate = indet;
    if (selectAllRefMobile.current)  selectAllRefMobile.current.indeterminate  = indet;
  }, [filteredItems, selectedIds]);

  function toggleOne(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelectedIds(prev => {
      const allVisibleSelected = filteredItems.length > 0 && filteredItems.every(it => prev.has(it._id));
      if (allVisibleSelected) {
        const next = new Set(prev);
        filteredItems.forEach(it => next.delete(it._id));
        return next;
      }
      const next = new Set(prev);
      filteredItems.forEach(it => next.add(it._id));
      return next;
    });
  }

  async function applyBulkStatus(newValue) {
    if (selectedIds.size === 0 || !newValue) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0, fail = 0;
    await chunkAll(ids, async (id) => {
      try {
        const res = await apiFetch(`${API_URL}/api/applications/${id}/status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newValue }),
        });
        if (res.ok) ok++; else fail++;
      } catch (e) {
        // session-expired throw triggers a reload — exit early so we don't
        // surface stale error text on a torn-down screen.
        if (e.message === "session-expired") return;
        fail++;
      }
    }, 6);
    // Optimistic UI: update affected rows in place + reflect "who set it" subtitle.
    const nowIso = new Date().toISOString();
    const myName = user?.name || user?.username || null;
    setItems(prev => prev.map(it => selectedIds.has(it._id) ? {
      ...it,
      status: newValue,
      lastStatusChange: {
        byName:     myName,
        byUsername: user?.username || null,
        oldStatus:  it.status,
        newStatus:  newValue,
        at:         nowIso,
      },
    } : it));
    setSelectedIds(new Set());
    setBulkBusy(false);
    // Surface result via the existing toast system (replaces the inline bulkErr badge).
    const label = STATUS_BY_VALUE[newValue]?.label || newValue;
    if (fail === 0) {
      addToast(`✓ ${ok} candidate${ok === 1 ? "" : "s"} updated to ${label}`, { type: "success" });
    } else if (ok === 0) {
      addToast(`✗ All ${fail} updates failed`, { type: "error" });
    } else {
      addToast(`${ok} updated to ${label}, ${fail} failed`, { type: "error" });
    }
  }

  function exportSelectedCsv() {
    const rowSet = selectedIds.size > 0 ? selectedIds : null;
    const rows = rowSet
      ? filteredItems.filter(it => rowSet.has(it._id))
      : filteredItems;
    const cols = [
      { key: "name",        label: "Name" },
      { key: "email",       label: "Email" },
      { key: "phone",       label: "Phone" },
      { key: "cnic",        label: "CNIC" },
      { key: "degree",      label: "Degree" },
      { key: "experienceYears", label: "Experience (y)" },
      { key: "score",       label: "Score" },
      { key: "max",         label: "Max" },
      { key: "percent",     label: "Percent" },
      { key: "submittedAt", label: "Applied" },
      { key: "status",      label: "Status",
        get: r => (STATUS_BY_VALUE[r.status]?.label || r.status || "") },
      { key: "flags",       label: "Flags",
        get: r => (r.flags || []).join("|") },
      { key: "llmScored",   label: "ScoredBy",
        get: r => (r.llmScored ? "llm" : "rule") },
    ];
    downloadCsv(
      `pvara-${activeRole}-${new Date().toISOString().slice(0, 10)}.csv`,
      rows, cols
    );
  }

  const activeRoleMeta = ROLES.find(r => r.id === activeRole);

  // Render the auth gate when not logged in (all hooks above run regardless to satisfy Rules of Hooks)
  if (!user) return <LoginGate />;

  // Top-level switcher — uses the same green-600 active style as the per-role tabs.
  const ViewSwitcher = (
    <div className="inline-flex gap-2">
      <button
        type="button"
        onClick={() => setViewMode("rubric")}
        className={
          "px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border transition whitespace-nowrap " +
          (viewMode === "rubric"
            ? "bg-green-600 text-white border-green-600"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
        }
      >
        Per-Role Review
      </button>
      <button
        type="button"
        onClick={() => setViewMode("chairperson")}
        className={
          "px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border transition whitespace-nowrap " +
          (viewMode === "chairperson"
            ? "bg-green-600 text-white border-green-600"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
        }
        title="Read-only digest of every advanced candidate, grouped by post"
      >
        Chairperson View
      </button>
    </div>
  );

  if (viewMode === "chairperson") {
    return (
      <div className="space-y-4 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">PVARA Rubric Screening</h1>
          {ViewSwitcher}
        </div>
        <ChairpersonView />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-5 border border-gray-100">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">PVARA Rubric Screening</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Pre-interview score per the official PVARA form (Sections 1 + 2 = 70 marks).
              Sections 3 &amp; 4 are interview-only.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {ViewSwitcher}
            <input
              type="search"
              placeholder="search name / email / CNIC"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-64"
            />
          </div>
        </div>

        {/* Filter chips: status + flagged + clear */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500 hidden sm:inline">Filter:</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setFlagsOnly(v => !v)}
            className={
              "px-2.5 py-1.5 rounded-md border text-xs " +
              (flagsOnly
                ? "bg-orange-50 border-orange-300 text-orange-800"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50")
            }
            title="Show only candidates with at least one flag"
          >
            ⚑ Flagged only
          </button>
          {/* Date-range filter — applies to submittedAt */}
          <span className="inline-flex items-center gap-1 border border-gray-300 rounded-md px-2 py-1 bg-white">
            <span className="text-gray-500">Applied</span>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="text-xs border-0 outline-none p-0 bg-transparent"
              aria-label="Applied from"
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="text-xs border-0 outline-none p-0 bg-transparent"
              aria-label="Applied to"
            />
          </span>
          {/* Mobile-friendly sort dropdown — desktop users also get column-header sort */}
          <select
            value={`${sortBy}:${sortDir}`}
            onChange={e => {
              const [b, d] = e.target.value.split(":");
              setSortBy(b); setSortDir(d);
            }}
            className="md:hidden border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
          >
            <option value="score:desc">Sort: Score ↓</option>
            <option value="score:asc">Sort: Score ↑</option>
            <option value="percent:desc">Sort: % ↓</option>
            <option value="percent:asc">Sort: % ↑</option>
            <option value="name:asc">Sort: Name A→Z</option>
            <option value="name:desc">Sort: Name Z→A</option>
            <option value="applied:desc">Sort: Applied (newest)</option>
            <option value="applied:asc">Sort: Applied (oldest)</option>
            <option value="status:asc">Sort: Status</option>
            <option value="flags:desc">Sort: Flags ↓</option>
          </select>
          {(search || statusFilter || flagsOnly || dateFrom || dateTo || sortBy !== "score" || sortDir !== "desc") && (
            <button
              type="button"
              onClick={() => { setSearch(""); setStatusFilter(""); setFlagsOnly(false); setDateFrom(""); setDateTo(""); setSortBy("score"); setSortDir("desc"); }}
              className="text-blue-600 hover:underline"
            >
              clear filters
            </button>
          )}
          <span className="ml-auto text-gray-500">
            Showing <b>{filteredItems.length}</b>{filteredItems.length !== items.length && <> of {items.length}</>}
          </span>
        </div>

        {/* Role tabs — horizontal scroll on tiny viewports rather than overflowing */}
        <div className="mt-4 -mx-1 overflow-x-auto">
          <div className="flex gap-2 px-1 min-w-min">
            {ROLES.map(r => (
              <button
                key={r.id}
                onClick={() => setActiveRole(r.id)}
                className={
                  "shrink-0 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium border transition whitespace-nowrap " +
                  (activeRole === r.id
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
                }
              >
                {r.short}
                <span className={"ml-1 sm:ml-2 text-[10px] sm:text-xs " + (activeRole === r.id ? "text-green-100" : "text-gray-500")}>
                  ({counts[r.id] ?? "…"})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Rubric summary banner */}
      {rubricSpec && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 max-w-full">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-sm text-blue-900 min-w-0 flex-1">
              <div className="font-semibold mb-1 break-words">Screening criteria for {rubricSpec.title}</div>
              <div className="text-xs leading-relaxed break-words">
                Each candidate is scored on the official PVARA evaluation form
                (SCF-2026): <b>Academic ({rubricSpec.sections?.[0]?.max_marks || 30} pts)</b> +
                {" "}<b>Experience ({rubricSpec.sections?.[1]?.max_marks || 40} pts)</b> = <b>70 auto-scored from CV</b>.
                Sections 3 &amp; 4 (Impact + Soft Skills, 30 pts) are <i>interview-only</i>.
                Min experience: <b>{rubricSpec.min_years_experience}y</b>.
              </div>
            </div>
            <button
              onClick={() => setRubricOpen(true)}
              className="px-3 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-50 whitespace-nowrap shrink-0"
            >
              📋 Rubric
            </button>
          </div>
        </div>
      )}

      {/* Active role detail */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-semibold">{activeRoleMeta?.title}</h2>
            <p className="text-xs text-gray-500">
              {filteredItems.length} candidate{filteredItems.length !== 1 ? "s" : ""}
              {sortBy === "score" && sortDir === "desc"
                ? " · sorted by pre-interview score"
                : ` · sorted by ${sortBy} ${sortDir === "asc" ? "↑" : "↓"}`}
            </p>
          </div>
          {error && <span className="text-xs text-red-600">Error: {error}</span>}
          {loading && <span className="text-xs text-gray-500">Loading…</span>}
          {/* Standalone CSV-export when nothing selected (exports current filtered view) */}
          {selectedIds.size === 0 && filteredItems.length > 0 && (
            <button
              type="button"
              onClick={exportSelectedCsv}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              title="Export the currently filtered list to CSV"
            >
              ⬇ Export CSV
            </button>
          )}
        </div>

        {/* Bulk action bar — only visible when something is selected */}
        {selectedIds.size > 0 && (
          <div className="px-5 py-2.5 border-b border-blue-200 bg-blue-50 flex flex-wrap items-center gap-3 text-xs">
            <span className="font-semibold text-blue-900">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2">
              <label className="text-blue-900">Set status:</label>
              <select
                disabled={bulkBusy}
                onChange={e => {
                  const v = e.target.value;
                  e.target.value = "";
                  if (!v) return;
                  const label = STATUS_BY_VALUE[v]?.label || v;
                  const n = selectedIds.size;
                  const ok = window.confirm(
                    `Set ${n} candidate${n === 1 ? "" : "s"} to "${label}"?\n\n` +
                    `A status-change email will be sent to each candidate. ` +
                    `This cannot be undone in bulk (you can revert individually).`
                  );
                  if (ok) applyBulkStatus(v);
                }}
                defaultValue=""
                className="border border-blue-300 rounded-md px-2 py-1 text-xs bg-white"
              >
                <option value="" disabled>— pick —</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={exportSelectedCsv}
              disabled={bulkBusy}
              className="px-3 py-1 border border-blue-300 bg-white rounded-md hover:bg-blue-100 disabled:opacity-50"
            >
              ⬇ Export CSV
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkBusy}
              className="text-blue-700 hover:underline disabled:opacity-50"
            >
              clear selection
            </button>
            {bulkBusy && <span className="text-blue-700">working…</span>}
          </div>
        )}

        {/* Mobile: card list (hidden on md+) */}
        <div className="md:hidden divide-y divide-gray-100">
          {/* Mobile select-all row */}
          {filteredItems.length > 0 && (
            <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-xs text-gray-600 cursor-pointer">
              <input
                ref={selectAllRefMobile}
                type="checkbox"
                checked={filteredItems.length > 0 && filteredItems.every(it => selectedIds.has(it._id))}
                onChange={toggleAllVisible}
              />
              <span>Select all visible</span>
            </label>
          )}
          {filteredItems.map((it, idx) => {
            const pct = it.percent;
            const cvHref = it.cv ? (it.cv.startsWith("http") ? it.cv : `${API_URL}${it.cv}`) : null;
            const isSelected = selectedIds.has(it._id);
            return (
              <div key={it._id} className={"flex items-start gap-2 " + (isSelected ? "bg-blue-50/40" : "")}>
                <label
                  className="pl-4 pt-5"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOne(it._id)}
                    aria-label={`Select ${it.name}`}
                  />
                </label>
                <button
                  onClick={() => openBreakdown(it._id)}
                  className="flex-1 text-left p-4 pl-2 hover:bg-gray-50 active:bg-gray-100"
                >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-xs text-gray-400">#{idx + 1}</span>
                      <span className="font-semibold text-gray-900 truncate" title={it.name}>{it.name}</span>
                      {it.source && it.source !== "pvara" && (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase tracking-wide"
                          title={`Imported from ${it.source}`}
                        >
                          {it.source}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {it.degree} · {it.experienceYears ?? "?"}y · CNIC {it.cnic}
                    </div>
                    {it.submittedAt && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        Applied {new Date(it.submittedAt).toLocaleDateString("en-GB", {day:"2-digit",month:"short",year:"2-digit"})}
                      </div>
                    )}
                    {/* Status + "set by" line — keeps mobile users informed of who did what */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      {(() => {
                        const s = STATUS_BY_VALUE[it.status];
                        return (
                          <span className={"text-[10px] px-1.5 py-0.5 rounded " + (s ? s.color : "bg-gray-100 text-gray-700")}>
                            {s ? s.label : (it.status || "—")}
                          </span>
                        );
                      })()}
                      {it.lastStatusChange && (it.lastStatusChange.byName || it.lastStatusChange.byUsername) && (
                        <span className="text-[10px] text-gray-500">
                          by {it.lastStatusChange.byName || it.lastStatusChange.byUsername} · {relTime(it.lastStatusChange.at)}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 space-x-3 text-xs">
                      {it.email && <a className="text-blue-600" href={`mailto:${it.email}`} onClick={e => e.stopPropagation()}>✉ email</a>}
                      {it.phone && <a className="text-gray-700" href={`tel:${it.phone}`} onClick={e => e.stopPropagation()}>📞 call</a>}
                      {cvHref && <a className="text-blue-600" href={cvHref} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>📄 CV</a>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={"text-lg font-bold " + colorForPercent(pct)}>{pct}%</div>
                    <div className="text-[11px] text-gray-500 font-mono">{it.score}/{it.max}</div>
                    {(it.flags || []).length > 0 && (
                      <div className="text-[11px] text-orange-700 mt-0.5">⚑ {it.flags.length}</div>
                    )}
                  </div>
                </div>
                </button>
              </div>
            );
          })}
          {!loading && filteredItems.length === 0 && (
            <div className="p-6 text-center text-gray-500 text-sm">No candidates scored for this role yet.</div>
          )}
        </div>

        {/* Desktop: full table (hidden on small screens) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th className="px-3 py-2 w-8">
                  <input
                    ref={selectAllRefDesktop}
                    type="checkbox"
                    aria-label="Select all visible"
                    checked={filteredItems.length > 0 && filteredItems.every(it => selectedIds.has(it._id))}
                    onChange={toggleAllVisible}
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-4 py-2">#</th>
                <SortableHeader id="name"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Name</SortableHeader>
                <th className="px-4 py-2">Contact</th>
                <th className="px-4 py-2 hidden lg:table-cell">CNIC</th>
                <th className="px-4 py-2">CV</th>
                <SortableHeader id="score"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">Score / 70</SortableHeader>
                <SortableHeader id="percent" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right">%</SortableHeader>
                <SortableHeader id="applied" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="hidden lg:table-cell">Applied</SortableHeader>
                <SortableHeader id="status"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} className="hidden lg:table-cell">Status</SortableHeader>
                <SortableHeader id="flags"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Flags</SortableHeader>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((it, idx) => {
                const pct = it.percent;
                const cvHref = it.cv ? (it.cv.startsWith("http") ? it.cv : `${API_URL}${it.cv}`) : null;
                return (
                  <tr key={it._id} className={"border-t border-gray-100 hover:bg-gray-50 " + (selectedIds.has(it._id) ? "bg-blue-50/40" : "") }>
                    <td className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        aria-label={`Select ${it.name}`}
                        checked={selectedIds.has(it._id)}
                        onChange={() => toggleOne(it._id)}
                        onClick={e => e.stopPropagation()}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        <span>{it.name}</span>
                        {it.source && it.source !== "pvara" && (
                          <span
                            className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase tracking-wide"
                            title={`Imported from ${it.source}`}
                          >
                            {it.source}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {it.degree} · {it.experienceYears ?? "?"}y
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {it.email && (
                        <div>
                          <a className="text-blue-600 hover:underline" href={`mailto:${it.email}`}>{it.email}</a>
                        </div>
                      )}
                      {it.phone && (
                        <div className="text-gray-600">
                          <a className="hover:underline" href={`tel:${it.phone}`}>{it.phone}</a>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap hidden lg:table-cell">{it.cnic}</td>
                    <td className="px-4 py-2 text-xs">
                      {cvHref ? (
                        <a
                          href={cvHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          title={it.cv}
                        >
                          📄 view
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{it.score}/{it.max}</td>
                    <td className={`px-4 py-2 text-right font-bold ${colorForPercent(pct)}`}>
                      <span className={"inline-block px-2 py-0.5 rounded " + bgForPercent(pct)}>{pct}%</span>
                      {it.llmScored && (
                        <span className="ml-1 text-[9px] align-top text-purple-700 font-semibold" title="Scored with LLM (context-aware). Rule-based fallback score also retained.">LLM</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 hidden lg:table-cell whitespace-nowrap">
                      {it.submittedAt ? new Date(it.submittedAt).toLocaleDateString("en-GB", {day:"2-digit",month:"short",year:"2-digit"}) : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs hidden lg:table-cell">
                      {(() => {
                        const s = STATUS_BY_VALUE[it.status];
                        return <span className={"inline-block px-2 py-0.5 rounded " + (s ? s.color : "bg-gray-100 text-gray-700")}>{s ? s.label : it.status}</span>;
                      })()}
                      {it.lastStatusChange && (it.lastStatusChange.byName || it.lastStatusChange.byUsername) && (
                        <div
                          className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[12rem]"
                          title={`${it.lastStatusChange.byName || it.lastStatusChange.byUsername} set ${STATUS_BY_VALUE[it.lastStatusChange.oldStatus]?.label || it.lastStatusChange.oldStatus || "—"} → ${STATUS_BY_VALUE[it.lastStatusChange.newStatus]?.label || it.lastStatusChange.newStatus || "—"} on ${formatDate(it.lastStatusChange.at)}`}
                        >
                          by {it.lastStatusChange.byName || it.lastStatusChange.byUsername} · {relTime(it.lastStatusChange.at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-orange-700">
                      {(it.flags || []).length > 0 && `⚑ ${it.flags.length}`}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => openBreakdown(it._id)}
                        className="text-blue-600 hover:underline text-xs"
                      >
                        breakdown →
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-500">No candidates scored for this role yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full Rubric drawer */}
      {rubricOpen && rubricSpec && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setRubricOpen(false)}>
          <div className="bg-white w-full sm:max-w-4xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-bold break-words">Rubric · {rubricSpec.title}</h3>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                  {rubricSpec.tier} · {rubricSpec.cadre || "—"} · Min {rubricSpec.min_years_experience}y experience
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                  Source: PVARA Evaluation Form SCF-2026 · CONFIDENTIAL
                </p>
              </div>
              <button onClick={() => setRubricOpen(false)} className="text-gray-400 hover:text-gray-700 text-3xl leading-none p-1 -mr-1 shrink-0">×</button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {(rubricSpec.sections || []).map(s => (
                <div key={s.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-sm font-mono">
                      {s.for_interview ? (
                        <span className="text-gray-500 italic">For Interview ({s.max_marks} pts)</span>
                      ) : (
                        <span>Max {s.max_marks} pts</span>
                      )}
                    </div>
                  </div>
                  {!s.for_interview && (
                    <div className="divide-y divide-gray-100">
                      {/* Render direct items */}
                      {(s.items || []).map(it => (
                        <RubricItemRow key={it.id} item={it} />
                      ))}
                      {/* Render sub_sections */}
                      {(s.sub_sections || []).map(sub => (
                        <div key={sub.id} className="bg-gray-50/40">
                          <div className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wide bg-gray-100/60">
                            {sub.id} · {sub.label}
                          </div>
                          {(sub.items || []).map(it => (
                            <RubricItemRow key={it.id} item={it} />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Breakdown drawer */}
      {breakdown && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setBreakdown(null)}>
          <div className="bg-white w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-bold break-words">{breakdown.name || "Loading…"}</h3>
                <p className="text-[11px] sm:text-xs text-gray-500">{breakdown.roleTitle}</p>
                {(breakdown.email || breakdown.phone || breakdown.cnic || breakdown.cv) && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {breakdown.email && (
                      <a href={`mailto:${breakdown.email}`} className="text-blue-600 hover:underline">✉ {breakdown.email}</a>
                    )}
                    {breakdown.phone && (
                      <a href={`tel:${breakdown.phone}`} className="text-gray-700 hover:underline">📞 {breakdown.phone}</a>
                    )}
                    {breakdown.cnic && (
                      <span className="text-gray-600">CNIC: {breakdown.cnic}</span>
                    )}
                    {breakdown.cv && (
                      <a
                        href={breakdown.cv.startsWith("http") ? breakdown.cv : `${API_URL}${breakdown.cv}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        📄 view CV
                      </a>
                    )}
                  </div>
                )}
                {breakdown.score != null && (
                  <p className="mt-2 text-sm">
                    Pre-Interview Score: <span className={"font-bold " + colorForPercent(breakdown.percent)}>
                      {breakdown.score}/{breakdown.max} ({breakdown.percent}%)
                    </span>
                  </p>
                )}
              </div>
              <button onClick={() => setBreakdown(null)} className="text-gray-400 hover:text-gray-700 text-3xl leading-none p-1 -mr-1 shrink-0">×</button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {breakdownLoading && <div className="text-gray-500">Loading breakdown…</div>}
              {breakdown.error && <div className="text-red-600">Error: {breakdown.error}</div>}

              {(breakdown.sections || []).map(s => (
                <div key={s.section_id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-sm font-mono">
                      {s.for_interview ? (
                        <span className="text-gray-500 italic">For Interview</span>
                      ) : (
                        <span>{s.score} / {s.max_marks}</span>
                      )}
                    </div>
                  </div>
                  {!s.for_interview && (
                    <table className="w-full text-xs">
                      <tbody>
                        {(s.items || []).map(it => (
                          <tr key={it.item_id} className="border-t border-gray-100">
                            <td className="px-4 py-2">
                              <div className="text-gray-800">{it.label}</div>
                              <div className="text-[11px] text-gray-500 mt-0.5">{it.evidence}</div>
                            </td>
                            <td className="px-4 py-2 text-right whitespace-nowrap font-mono">
                              <span className={(it.score === 0 ? "text-gray-400" : "text-gray-900")}>
                                {it.score}/{it.max_marks}
                              </span>
                              {it.matched_tier && (
                                <div className="text-[10px] text-blue-600">[{it.matched_tier}]</div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}

              {(breakdown.flags || []).length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="font-semibold text-orange-800 text-sm mb-1">⚑ Rubric Flags</div>
                  <ul className="text-xs text-orange-900 list-disc pl-5 space-y-1">
                    {breakdown.flags.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}

              {/* Status + Application metadata */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="font-semibold text-gray-800 text-sm mb-2">Status & submission</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500 mb-1">Status</div>
                    <StatusDropdown
                      applicationId={breakdown.applicationId}
                      current={breakdown.status}
                      onChanged={(newVal) => {
                        const nowIso = new Date().toISOString();
                        const myName = user?.name || user?.username || null;
                        const entry = {
                          byName:     myName,
                          byUsername: user?.username || null,
                          oldStatus:  breakdown?.status,
                          newStatus:  newVal,
                          at:         nowIso,
                        };
                        // Drawer: bump current status + prepend history entry locally
                        setBreakdown(b => b ? {
                          ...b,
                          status: newVal,
                          statusHistory: [entry, ...(b.statusHistory || [])],
                        } : b);
                        // List: keep the table row's "by X · ts" subtitle in sync
                        setItems(prev => prev.map(it => it._id === breakdown?.applicationId ? {
                          ...it,
                          status: newVal,
                          lastStatusChange: entry,
                        } : it));
                      }}
                    />
                  </div>
                  <div>
                    <div className="text-gray-500 mb-1">Applied</div>
                    <div className="text-gray-900">{formatDate(breakdown.submittedAt)}</div>
                  </div>
                  {breakdown.updatedAt && breakdown.updatedAt !== breakdown.submittedAt && (
                    <div>
                      <div className="text-gray-500 mb-1">Last updated</div>
                      <div className="text-gray-900">{formatDate(breakdown.updatedAt)}</div>
                    </div>
                  )}
                </div>

                {/* Status change history — who set what, when */}
                {Array.isArray(breakdown.statusHistory) && breakdown.statusHistory.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-700 mb-1.5">Status history</div>
                    <ul className="space-y-1 text-[11px] text-gray-700">
                      {breakdown.statusHistory.map((h, i) => {
                        const oldL = STATUS_BY_VALUE[h.oldStatus]?.label || h.oldStatus || "—";
                        const newL = STATUS_BY_VALUE[h.newStatus]?.label || h.newStatus || "—";
                        return (
                          <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-gray-500">{formatDate(h.at)}</span>
                            <span className="text-gray-400">·</span>
                            <span><b>{h.byName || h.byUsername || "unknown"}</b></span>
                            <span className="text-gray-400">set</span>
                            <span className="font-medium">{oldL}</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-medium">{newL}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Notes */}
              <NotesPanel
                applicationId={breakdown.applicationId}
                notes={breakdown.notes || []}
                onAdded={(note) => setBreakdown(b => b ? {...b, notes: [...(b.notes || []), note]} : b)}
              />

              {breakdown.profileSnapshot && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="font-semibold text-gray-800 text-sm mb-2">Profile snapshot (rule-based extraction)</div>
                  <pre className="text-[11px] text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(breakdown.profileSnapshot, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PvaraRubricView;
